import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { loginUser } from "../loginSlice";
import { saveResult } from "../../services/saveResult";
import { addReportToServer } from "../../functions/sendReport";
import {
  completeUnhealthyStage,
  ensureUnhealthyGame,
  getUnhealthyGameState,
  saveUnhealthyGameProgress,
} from "../../services/unhealthyGameProgress";
import GameHUD from "../../components/unhealthy/GameHUD";
import MapScreen from "../../components/unhealthy/MapScreen";
import CommonSuccessPopup from "../../components/unhealthy/CommonSuccessPopup";
import { stagePageComponents } from "../../components/unhealthy/stagePages";
import basketballBg from "../../assets/game/bashkelball_bgpng.png";
import gymBg from "../../assets/game/gym_bg.png";
import marketBg from "../../assets/game/market_bg.jpg";
import marketMobileBg from "../../assets/game/market-mobile_bg.png";
import {
  mapHotspotsDesktop,
  mapHotspotsMobile,
  stageRegistry,
  STAGE_IDS,
  unhealthyGameConfig,
} from "../../data/unhealthyGameData";
import "./StaticGamePage.css";

const STAGE_MAX = STAGE_IDS.reduce((acc, id) => {
  acc[id] = stageRegistry[id]?.maxScore || 0;
  return acc;
}, {});

function stageOrder() {
  return STAGE_IDS;
}

function pickStageBackground(stageId, isMobile) {
  const stage = stageRegistry[stageId];
  if (!stage) return "";
  if (stageId === "basketball") return basketballBg;
  if (stageId === "gymnasium") return gymBg;
  if (stageId === "supermarket") return isMobile ? marketMobileBg : marketBg;
  if (stageId === "yoga") {
    return isMobile
      ? stage.data?.backgroundMobile || stage.data?.background || ""
      : stage.data?.backgroundDesktop || stage.data?.background || "";
  }
  if (stageId === "restaurants") {
    return isMobile
      ? stage.data?.backgroundMobile || stage.data?.background || ""
      : stage.data?.background || "";
  }
  if (stage.data?.background) return stage.data.background;
  if (isMobile && stage.assets?.mobile) return stage.assets.mobile;
  return stage.assets?.desktop || "";
}

function makeUserId() {
  const key = "unhealthy_react_user";
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const generated = `ul_${Date.now()}`;
  localStorage.setItem(key, generated);
  return generated;
}

function parseStoredTimeToSeconds(v) {
  if (!v) return 0;
  const parts = String(v)
    .trim()
    .split(":")
    .map((x) => Number(x) || 0);
  if (parts.length === 3) return Math.max(0, parts[0] * 3600 + parts[1] * 60 + parts[2]);
  if (parts.length === 2) return Math.max(0, parts[0] * 60 + parts[1]);
  return 0;
}

function appBaseUrl() {
  const base = (process.env.REACT_APP_BASE_URL || "").trim().replace(/\/+$/, "");
  return base || window.location.origin;
}

function demoPlansUrl() {
  return `${appBaseUrl()}/plans`;
}

const STAGE_COMPLETE_POPUP_DELAY_MS = 0;

export default function StaticGamePage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { stageId: stageIdParam } = useParams();
  const { status, user } = useSelector((state) => state.auth);
  const [screen, setScreen] = useState(() => (stageIdParam ? "stage" : "map"));
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [completed, setCompleted] = useState({});
  const [saving, setSaving] = useState(false);
  /** Authoritative total points from API (per-stage cumulative). */
  const [pointsTotal, setPointsTotal] = useState(0);
  /** Points earned in the current basketball session (before submit). Shown in HUD only. */
  const [basketballSessionPoints, setBasketballSessionPoints] = useState(0);
  /** Points earned in the current yoga session (before submit). Shown in HUD only. */
  const [yogaSessionPoints, setYogaSessionPoints] = useState(0);
  /** Points earned in the current swimming session (before submit). Shown in HUD only. */
  const [swimmingSessionPoints, setSwimmingSessionPoints] = useState(0);
  /** Points earned in the current gym session (before submit). Shown in HUD only. */
  const [gymSessionPoints, setGymSessionPoints] = useState(0);
  /** Points earned in the current supermarket session (before submit). Shown in HUD only. */
  const [supermarketSessionPoints, setSupermarketSessionPoints] = useState(0);
  /** Points earned in the current restaurant session (before submit). Shown in HUD only. */
  const [restaurantSessionPoints, setRestaurantSessionPoints] = useState(0);
  /** Frozen HUD score while stage completion/redirect is in progress. */
  const [pendingDisplayScore, setPendingDisplayScore] = useState(null);
  const [stageCompletePopupOpen, setStageCompletePopupOpen] = useState(false);
  const [stageCompletionPending, setStageCompletionPending] = useState(false);
  const [pendingStagePersistPromise, setPendingStagePersistPromise] = useState(null);
  const [pendingStageIsDemoUser, setPendingStageIsDemoUser] = useState(false);
  const [pendingStageIsFinalCompletion, setPendingStageIsFinalCompletion] = useState(false);
  const [isMapMobile, setIsMapMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false
  );
  const elapsedRef = useRef(0);
  const pointsRef = useRef(0);
  const reportIdRef = useRef(null);
  const currentStage = STAGE_IDS.includes(stageIdParam) ? stageIdParam : "";

  const totalScore = useMemo(() => {
    const localSum = Object.values(completed).reduce((acc, entry) => acc + (entry.score || 0), 0);
    return Math.max(pointsTotal, localSum);
  }, [completed, pointsTotal]);
  const attemptedStages = Object.keys(completed).length;
  const allDone = attemptedStages === stageOrder().length;

  useEffect(() => {
    elapsedRef.current = elapsedSeconds;
  }, [elapsedSeconds]);

  useEffect(() => {
    pointsRef.current = totalScore;
  }, [totalScore]);

  const hudScore = (() => {
    if (stageCompletionPending) return pendingDisplayScore ?? totalScore;
    if (screen !== "stage") return totalScore;
    if (currentStage === "basketball") return totalScore + basketballSessionPoints;
    if (currentStage === "yoga") return totalScore + yogaSessionPoints;
    if (currentStage === "swimming") return totalScore + swimmingSessionPoints;
    if (currentStage === "gymnasium") return totalScore + gymSessionPoints;
    if (currentStage === "supermarket") return totalScore + supermarketSessionPoints;
    if (currentStage === "restaurants") return totalScore + restaurantSessionPoints;
    return totalScore;
  })();

  useEffect(() => {
    if (!(screen === "stage" && currentStage === "basketball")) {
      setBasketballSessionPoints(0);
    }
    if (!(screen === "stage" && currentStage === "yoga")) {
      setYogaSessionPoints(0);
    }
    if (!(screen === "stage" && currentStage === "swimming")) {
      setSwimmingSessionPoints(0);
    }
    if (!(screen === "stage" && currentStage === "gymnasium")) {
      setGymSessionPoints(0);
    }
    if (!(screen === "stage" && currentStage === "supermarket")) {
      setSupermarketSessionPoints(0);
    }
    if (!(screen === "stage" && currentStage === "restaurants")) {
      setRestaurantSessionPoints(0);
    }
  }, [screen, currentStage]);

  useEffect(() => {
    if (screen !== "stage") {
      setStageCompletionPending(false);
      setStageCompletePopupOpen(false);
      setPendingDisplayScore(null);
      setPendingStageIsFinalCompletion(false);
    }
  }, [screen, currentStage]);

  useEffect(() => {
    if (status === "idle" && !user) {
      dispatch(loginUser());
    }
  }, [dispatch, status, user]);

  useEffect(() => {
    if (status === "failed") {
      navigate("/login", { replace: true });
    }
  }, [status, navigate]);

  useEffect(() => {
    if (status === "succeeded" && !user) {
      navigate("/login", { replace: true });
    }
  }, [status, user, navigate]);

  useEffect(() => {
    if (stageIdParam && !currentStage) {
      navigate("/game", { replace: true });
      return;
    }
    if (currentStage) {
      setScreen("stage");
    } else if (screen === "stage") {
      setScreen("map");
    }
  }, [currentStage, navigate, screen, stageIdParam]);

  useEffect(() => {
    if (screen !== "map") return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [screen]);

  useEffect(() => {
    if (screen !== "map" && screen !== "stage") return undefined;
    const id = setInterval(() => setElapsedSeconds((prev) => prev + 1), 1000);
    return () => clearInterval(id);
  }, [screen]);

  useEffect(() => {
    const onResize = () => setIsMapMobile(window.innerWidth <= 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /** Full-page background for stage screens (map uses its own image layer). */
  useEffect(() => {
    if (screen !== "stage") return undefined;
    const body = document.body;
    const prev = {
      backgroundImage: body.style.backgroundImage,
      backgroundSize: body.style.backgroundSize,
      backgroundPosition: body.style.backgroundPosition,
      backgroundRepeat: body.style.backgroundRepeat,
      backgroundAttachment: body.style.backgroundAttachment,
      minHeight: body.style.minHeight,
    };

    const applyBackground = () => {
      const isMobile = typeof window !== "undefined" ? window.innerWidth <= 768 : false;
      const bg =
        pickStageBackground(currentStage, isMobile) ||
        unhealthyGameConfig.mapBackgroundDesktop ||
        unhealthyGameConfig.mapBackground;
      if (!bg) return;
      body.style.backgroundImage = `url("${bg}")`;
      body.style.backgroundSize = "cover";
      body.style.backgroundPosition = "center center";
      body.style.backgroundRepeat = "no-repeat";
      body.style.backgroundAttachment = isMobile ? "scroll" : "fixed";
      body.style.minHeight = "100vh";
    };

    applyBackground();
    window.addEventListener("resize", applyBackground);
    return () => {
      window.removeEventListener("resize", applyBackground);
      body.style.backgroundImage = prev.backgroundImage;
      body.style.backgroundSize = prev.backgroundSize;
      body.style.backgroundPosition = prev.backgroundPosition;
      body.style.backgroundRepeat = prev.backgroundRepeat;
      body.style.backgroundAttachment = prev.backgroundAttachment;
      body.style.minHeight = prev.minHeight;
    };
  }, [screen, currentStage]);

  useEffect(() => {
    if (!user) return undefined;
    if (user.reportId) {
      reportIdRef.current = user.reportId;
      return undefined;
    }
    if (user.userId && user.sessionId) {
      try {
        const saved = sessionStorage.getItem(`tot_report_${user.sessionId}_${user.userId}`);
        if (saved) reportIdRef.current = saved;
      } catch {
        /* ignore */
      }
    }
    return undefined;
  }, [user?.reportId, user?.userId, user?.sessionId]);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const uid = user.userId || makeUserId();
        await ensureUnhealthyGame({
          userId: uid,
          email: user.email,
          organizationId: user.organizationId,
          sessionId: user.sessionId,
          name: user.name,
        });
        const st = await getUnhealthyGameState({
          userId: uid,
          sessionId: user.sessionId,
          organizationId: user.organizationId,
        });
        if (cancelled || !st.data) return;
        const syncedPoints = Number(st.data.points) || 0;
        setPointsTotal(syncedPoints);
        setElapsedSeconds(parseStoredTimeToSeconds(st.data.time));
        const next = {};
        Object.entries(st.data.stages || {}).forEach(([id, s]) => {
          if (s === "played" && STAGE_MAX[id] != null) {
            // Stage is completed, but score source of truth is DB `points` (not per-stage max).
            next[id] = { score: 0, max: STAGE_MAX[id] };
          }
        });
        setCompleted((prev) => ({ ...prev, ...next }));
        // Ensure a report exists at initial insert/load and remains updated on resume.
        await addReportToServer(user, {
          points: syncedPoints,
          elapsedSeconds: parseStoredTimeToSeconds(st.data.time),
          answers: [],
          reportIdRef,
        });
      } catch (e) {
        console.warn("unhealthy game sync:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const persistProgressOnce = async (pointsValue) => {
    if (!user) return;
    try {
      const uid = user.userId || makeUserId();
      const res = await saveUnhealthyGameProgress({
        userId: uid,
        email: user.email,
        organizationId: user.organizationId,
        sessionId: user.sessionId,
        name: user.name,
        elapsedSeconds: elapsedRef.current,
        points: Math.max(0, Number(pointsValue) || 0),
      });
      if (res?.success && res.data?.points != null) {
        const syncedPoints = Number(res.data.points) || 0;
        setPointsTotal((prev) => Math.max(prev, syncedPoints));
        await addReportToServer(user, {
          points: syncedPoints,
          elapsedSeconds: elapsedRef.current,
          answers: [],
          reportIdRef,
        });
      }
    } catch (_e) {
      // Do not interrupt gameplay for transient network/API issues.
    }
  };

  const navigateToThankYou = async () => {
    if (saving) return;
    setSaving(true);
    const payload = {
      userId: user?.userId || makeUserId(),
      sessionId: user?.sessionId || "unhealthy-static-react",
      organizationId: user?.organizationId || "default-org",
      points: totalScore,
      totalQuestions: unhealthyGameConfig.maxScore,
      attemptedQuestions: attemptedStages,
      timeLeft: elapsedSeconds,
      percentage: Math.round((totalScore / unhealthyGameConfig.maxScore) * 100),
    };

    try {
      await saveResult(payload);
      await persistProgressOnce(totalScore);
    } catch (e) {
      // Ignore API failure for route flow; result screen still shows local score.
    }

    navigate(`/unhealthy-thankyou${location.search || ""}`, {
      replace: true,
      state: {
        score: totalScore,
        totalQuestions: unhealthyGameConfig.maxScore,
        pointsPerCorrect: 1,
        passScorePercent: 60,
        attemptedQuestions: attemptedStages,
        elapsedSeconds,
        completed,
      },
    });
  };

  const softSaveProgress = async (pointsValue, attemptedValue) => {
    try {
      await saveResult({
        userId: user?.userId || makeUserId(),
        sessionId: user?.sessionId || "unhealthy-static-react",
        organizationId: user?.organizationId || "default-org",
        points: pointsValue,
        totalQuestions: unhealthyGameConfig.maxScore,
        attemptedQuestions: attemptedValue,
        timeLeft: elapsedSeconds,
        percentage: Math.round((pointsValue / unhealthyGameConfig.maxScore) * 100),
      });
    } catch (_e) {
      // Keep game flow smooth even when persistence API is temporarily unavailable.
    }
  };

  const submitStage = async (stageId, rawScore) => {
    const score = Math.max(0, Math.min(STAGE_MAX[stageId] || 0, Number(rawScore) || 0));
    const uid = user?.userId || makeUserId();
    const alreadyDone = Boolean(completed[stageId]);
    const nextPoints = alreadyDone ? totalScore : totalScore + score;
    const nextAttempted = alreadyDone ? attemptedStages : attemptedStages + 1;
    let shouldApplyLocally = true;
    let persisted = false;

    try {
      const res = await completeUnhealthyStage({
        userId: uid,
        email: user?.email,
        organizationId: user?.organizationId,
        sessionId: user?.sessionId,
        name: user?.name,
        role: user?.role || user?.source || "GUEST_USER",
        token: user?.token || "",
        gameId: user?.gameId || null,
        stageId,
        stagePoints: score,
        elapsedSeconds,
      });
      if (res.success && res.data?.points != null) {
        setPointsTotal(Number(res.data.points) || 0);
        await addReportToServer(user, {
          points: Number(res.data.points) || 0,
          elapsedSeconds,
          answers: [stageId],
          reportIdRef,
        });
      }
      await persistProgressOnce(res?.data?.points ?? nextPoints);
      persisted = true;
    } catch (e) {
      if (e.status === 409 || e.code === "STAGE_ALREADY_PLAYED") {
        shouldApplyLocally = false;
        await persistProgressOnce(totalScore);
        persisted = true;
      } else {
        console.error(e);
        // Fallback: still persist points/time in generic game result row and continue route flow.
        await softSaveProgress(nextPoints, nextAttempted);
        await persistProgressOnce(nextPoints);
        setPointsTotal(nextPoints);
        persisted = false;
      }
    }

    if (shouldApplyLocally) {
      setCompleted((prev) => ({
        ...prev,
        [stageId]: { score, max: STAGE_MAX[stageId] || 0 },
      }));
    }
    return persisted;
  };

  const handleStageDone = (score) => {
    const stageId = currentStage;
    if (!stageId || stageCompletionPending) return;
    const isDemoUser = user?.source === "DEMO";
    const safeScore = Math.max(0, Math.min(STAGE_MAX[stageId] || 0, Number(score) || 0));
    const alreadyDone = Boolean(completed[stageId]);
    const isFinalStageCompletion =
      alreadyDone ? allDone : attemptedStages + 1 === stageOrder().length;
    const nextDisplayScore = alreadyDone ? totalScore : totalScore + safeScore;
    setPendingDisplayScore(nextDisplayScore);
    setStageCompletionPending(true);
    const persistPromise = submitStage(stageId, score);
    const shouldShowSuccessPopup = Number(score) > 0;

    if (!shouldShowSuccessPopup) {
      (async () => {
        try {
          await persistPromise;
        } finally {
          if (isDemoUser) {
            await Swal.fire({
              icon: "success",
              title: "Thank you for playing.",
              text: "Subscribe to any PLAN to play with your peers.",
            });
            window.location.replace(demoPlansUrl());
            return;
          }
          if (isFinalStageCompletion) {
            setStageCompletionPending(false);
            setPendingDisplayScore(null);
            await navigateToThankYou();
            return;
          }
          setScreen("map");
          navigate("/game", { replace: true });
          setStageCompletionPending(false);
          setPendingDisplayScore(null);
        }
      })();
      return;
    }

    window.setTimeout(() => {
      setStageCompletePopupOpen(true);
    }, STAGE_COMPLETE_POPUP_DELAY_MS);
    setPendingStagePersistPromise(() => persistPromise);
    setPendingStageIsDemoUser(isDemoUser);
    setPendingStageIsFinalCompletion(isFinalStageCompletion);
  };

  const handleSuccessPopupOk = async () => {
    setStageCompletePopupOpen(false);
    try {
      if (pendingStagePersistPromise) {
        await pendingStagePersistPromise;
      }
    } finally {
      setPendingStagePersistPromise(null);
      if (pendingStageIsDemoUser) {
        setPendingStageIsDemoUser(false);
        setPendingStageIsFinalCompletion(false);
        await Swal.fire({
          icon: "success",
          title: "Thank you for playing.",
          text: "Subscribe to any PLAN to play with your peers.",
        });
        window.location.replace(demoPlansUrl());
        return;
      }
      setPendingStageIsDemoUser(false);
      setStageCompletionPending(false);
      setPendingDisplayScore(null);
      if (pendingStageIsFinalCompletion) {
        setPendingStageIsFinalCompletion(false);
        await navigateToThankYou();
        return;
      }
      setPendingStageIsFinalCompletion(false);
      setScreen("map");
      navigate("/game", { replace: true });
    }
  };

  const renderStage = () => {
    const StagePage = stagePageComponents[currentStage];
    const stage = stageRegistry[currentStage];
    if (!StagePage || !stage) return null;
    return (
      <StagePage
        stage={stage}
        onSessionPointsChange={
          currentStage === "basketball"
            ? setBasketballSessionPoints
            : currentStage === "yoga"
              ? setYogaSessionPoints
            : currentStage === "swimming"
              ? setSwimmingSessionPoints
              : currentStage === "gymnasium"
                ? setGymSessionPoints
                : currentStage === "supermarket"
                  ? setSupermarketSessionPoints
                  : currentStage === "restaurants"
                    ? setRestaurantSessionPoints
              : undefined
        }
        onDone={handleStageDone}
      />
    );
  };

  if (status === "loading" || (status === "idle" && !user)) {
    return (
      <main className="unhealthy-main unhealthy-main--fill">
        <div className="quiz-loader-container">
          <div className="quiz-loader" />
        </div>
      </main>
    );
  }

  if (!user) {
    return null;
  }

  if (screen === "landing") {
    return (
      <main className="unhealthy-landing">
        <img src={unhealthyGameConfig.logoGif} alt="Escape Game" className="unhealthy-logo" />
        <button type="button" className="stage-submit" onClick={() => setScreen("intro")}>
          Begin Play
        </button>
      </main>
    );
  }

  if (screen === "intro") {
    return (
      <main className="unhealthy-intro">
        <video className="intro-video" src={unhealthyGameConfig.introVideo} autoPlay muted playsInline />
        <button type="button" className="stage-submit intro-next" onClick={() => setScreen("map")}>
          Next
        </button>
      </main>
    );
  }

  const isImmersiveStage =
    currentStage === "basketball" || currentStage === "swimming" || currentStage === "walking";

  const mainClass =
    screen === "map"
      ? "unhealthy-main unhealthy-main--map"
      : screen === "stage"
        ? `unhealthy-main unhealthy-main--stage${currentStage === "basketball" ? " unhealthy-main--basketball-stage" : ""}`
        : "unhealthy-main";

  return (
    <main className={mainClass}>
      {screen === "map" ? (
        <div className="unhealthy-map-root">
          <GameHUD variant="map" elapsedSeconds={elapsedSeconds} score={totalScore} />
          <MapScreen
            background={
              isMapMobile
                ? unhealthyGameConfig.mapBackgroundMobile || unhealthyGameConfig.mapBackground
                : unhealthyGameConfig.mapBackgroundDesktop || unhealthyGameConfig.mapBackground
            }
            hotspots={isMapMobile ? mapHotspotsMobile : mapHotspotsDesktop}
            completed={completed}
            onOpenStage={(stageId) => {
              if (completed[stageId]) return;
              navigate(`/game/stage/${stageId}`);
            }}
          />
        </div>
      ) : null}
      {screen === "stage" ? (
        <>
          <GameHUD
            variant="stage"
            elapsedSeconds={elapsedSeconds}
            score={hudScore}
          />
          <section
            className={`stage-shell stage-shell--flush${isImmersiveStage ? " stage-shell--immersive" : ""}`}
          >
            {renderStage()}
          </section>
        </>
      ) : null}
      {saving ? <div className="saving-overlay">Saving result...</div> : null}
      <CommonSuccessPopup
        open={stageCompletePopupOpen}
        message="Well played. Let's keep going!"
        onClose={handleSuccessPopupOk}
      />
    </main>
  );
}
